// A small zip writer and reader for the AI tools download (owner 2026-10-23:
// the download is the folder the tools write locally, as one .zip). No
// dependency: PKWARE's format (APPNOTE 6.3) with stored entries, or deflated
// ones where the browser can deflate (CompressionStream 'deflate-raw') and
// that makes them smaller; CRC-32; UTF-8 names (general purpose bit 11). No
// zip64: a download past 65,535 entries or 4 GB is refused in words.
//
// buildZip(entries, { deflate, now }) -> Promise<Uint8Array[]> (the parts of
// a Blob). entries: [{ name, data }] in order, data a string (written as
// UTF-8) or a Uint8Array; a name ending in "/" is a folder. deflate(bytes) ->
// Promise<Uint8Array> of raw deflate, or null to store everything (the
// default is the browser's, when it has one). `stored` names entries that are
// always stored (export.json, so readZipText can read it without inflating).

const encoder = new TextEncoder()

const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let n = 0; n < 256; n++) {
    let c = n
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[n] = c >>> 0
  }
  return table
})()

export const crc32 = bytes => {
  let crc = 0xffffffff
  for (let i = 0; i < bytes.length; i++) crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8)
  return (crc ^ 0xffffffff) >>> 0
}

// The browser's raw deflate, or null where there is none.
export const browserDeflate = typeof CompressionStream === 'function' && typeof Response === 'function' && typeof Blob === 'function'
  ? async bytes => new Uint8Array(await new Response(new Blob([bytes]).stream().pipeThrough(new CompressionStream('deflate-raw'))).arrayBuffer())
  : null

// MS-DOS date and time (local, two-second steps), as zip keeps them.
const dosTime = date => ((date.getHours() << 11) | (date.getMinutes() << 5) | (date.getSeconds() >> 1)) & 0xffff
const dosDate = date => ((Math.max(0, date.getFullYear() - 1980) << 9) | ((date.getMonth() + 1) << 5) | date.getDate()) & 0xffff

const MAX_ENTRIES = 0xffff
const MAX_BYTES = 0xffffffff
export const TOO_BIG = 'This download is too large for one zip file. Narrow the filters and download again.'

export const buildZip = async (entries, { deflate = browserDeflate, now = new Date(), stored = [] } = {}) => {
  if (entries.length > MAX_ENTRIES) throw new Error(TOO_BIG)
  const time = dosTime(now)
  const date = dosDate(now)
  const parts = []
  const central = []
  let offset = 0

  for (const entry of entries) {
    const folder = entry.name.endsWith('/')
    const name = encoder.encode(entry.name)
    const raw = folder ? new Uint8Array(0) : typeof entry.data === 'string' ? encoder.encode(entry.data) : entry.data
    const crc = crc32(raw)
    let method = 0
    let body = raw
    if (!folder && deflate && raw.length > 0 && !stored.includes(entry.name)) {
      try {
        const packed = await deflate(raw)
        if (packed && packed.length < raw.length) { method = 8; body = packed }
      } catch (err) {
        // stored, then
      }
    }
    if (offset + 30 + name.length + body.length > MAX_BYTES) throw new Error(TOO_BIG)
    const version = method === 8 ? 20 : folder ? 20 : 10

    const local = new DataView(new ArrayBuffer(30))
    local.setUint32(0, 0x04034b50, true)
    local.setUint16(4, version, true)
    local.setUint16(6, 0x0800, true)
    local.setUint16(8, method, true)
    local.setUint16(10, time, true)
    local.setUint16(12, date, true)
    local.setUint32(14, crc, true)
    local.setUint32(18, body.length, true)
    local.setUint32(22, raw.length, true)
    local.setUint16(26, name.length, true)
    local.setUint16(28, 0, true)
    parts.push(new Uint8Array(local.buffer), name, body)

    const head = new DataView(new ArrayBuffer(46))
    head.setUint32(0, 0x02014b50, true)
    head.setUint16(4, 20, true)
    head.setUint16(6, version, true)
    head.setUint16(8, 0x0800, true)
    head.setUint16(10, method, true)
    head.setUint16(12, time, true)
    head.setUint16(14, date, true)
    head.setUint32(16, crc, true)
    head.setUint32(20, body.length, true)
    head.setUint32(24, raw.length, true)
    head.setUint16(28, name.length, true)
    head.setUint16(30, 0, true)
    head.setUint16(32, 0, true)
    head.setUint16(34, 0, true)
    head.setUint16(36, 0, true)
    head.setUint32(38, folder ? 0x10 : 0, true)
    head.setUint32(42, offset, true)
    central.push(new Uint8Array(head.buffer), name)

    offset += 30 + name.length + body.length
  }

  const centralSize = central.reduce((n, part) => n + part.length, 0)
  if (offset + centralSize + 22 > MAX_BYTES) throw new Error(TOO_BIG)
  const end = new DataView(new ArrayBuffer(22))
  end.setUint32(0, 0x06054b50, true)
  end.setUint16(4, 0, true)
  end.setUint16(6, 0, true)
  end.setUint16(8, entries.length, true)
  end.setUint16(10, entries.length, true)
  end.setUint32(12, centralSize, true)
  end.setUint32(16, offset, true)
  end.setUint16(20, 0, true)
  return [...parts, ...central, new Uint8Array(end.buffer)]
}

// Whether bytes start a zip ("PK\3\4").
export const isZip = bytes => bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04

// The text of one stored entry of a zip (bytes: a Uint8Array of the whole
// file), or null when it has none by that name or it is compressed. What the
// mark dialog reads export.json with: buildZip always stores that one.
export const readZipText = (bytes, wanted) => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  let end = -1
  for (let i = bytes.length - 22; i >= Math.max(0, bytes.length - 22 - 0xffff); i--) {
    if (view.getUint32(i, true) === 0x06054b50) { end = i; break }
  }
  if (end < 0) return null
  const count = view.getUint16(end + 10, true)
  let at = view.getUint32(end + 16, true)
  const decoder = new TextDecoder()
  for (let n = 0; n < count && at + 46 <= bytes.length; n++) {
    if (view.getUint32(at, true) !== 0x02014b50) return null
    const method = view.getUint16(at + 10, true)
    const size = view.getUint32(at + 20, true)
    const nameLength = view.getUint16(at + 28, true)
    const extra = view.getUint16(at + 30, true)
    const comment = view.getUint16(at + 32, true)
    const local = view.getUint32(at + 42, true)
    const name = decoder.decode(bytes.subarray(at + 46, at + 46 + nameLength))
    if (name === wanted) {
      if (method !== 0 || view.getUint32(local, true) !== 0x04034b50) return null
      const start = local + 30 + view.getUint16(local + 26, true) + view.getUint16(local + 28, true)
      return decoder.decode(bytes.subarray(start, start + size))
    }
    at += 46 + nameLength + extra + comment
  }
  return null
}
